//@flow
import * as Immutable from 'immutable';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import {
    MasterHomebase,
    HomebaseType,
    CrossDocksType
} from 'common/models/internal/MasterHomebase';
import DataUtils from 'common/utils/DataUtils';
import { LatLng } from 'common/models/internal/LatLng';
import { EntityImportHelper } from 'common/entity/helpers/EntityImportHelper';
import type { CrossDocksTypeEnum } from 'common/models/flow/CrossDockInfo';

export class HomebaseImportHelper extends EntityImportDataHelper {
    static processType = ({ cellValue }: { cellValue: ?string }) => {
        if (cellValue) {
            const translatedHomebaseTypes = HomebaseType.getOptions({
                translationKey: 'masterHomebaseProperties.types'
            });
            const matchedHomebase = translatedHomebaseTypes.find(
                ({ label }) => label === cellValue
            );
            if (matchedHomebase) {
                return matchedHomebase.value;
            }
            return ' '; // HACK to handle incorrect value
        }
        return cellValue;
    };

    static getTimeSlotsAccessor = (accessor: {
        accessor: Array<string>,
        entity: MasterHomebase
    }) =>
        EntityImportDataHelper.getTimeSlotsAccessor({
            ...accessor,
            workingDaysAccessor: ['timeSlots'],
            dayOfWeekIndex: 1
        });

    static processEntityRows = (
        {
            rows
        }: {
            rows: Immutable.List<MasterHomebase>
        },
        { clientId }
    ): Immutable.List<MasterHomebase> => {
        return rows.map(entityRow => {
            const updatedHomebaseRow = entityRow.map((cellValue, headerId) => {
                switch (headerId) {
                    case 'timeSlots':
                        return cellValue.toList();
                    case 'latLng':
                        return LatLng.sanitizeLatLng(cellValue.toJS());
                    case 'customProperties':
                        return cellValue.filter(value => value);
                    case 'contactId':
                        return cellValue &&
                            !DataUtils.isEmpty(cellValue.get('contactId'))
                            ? cellValue.set('clientId', clientId)
                            : null;
                    case 'contact':
                        return cellValue &&
                            !DataUtils.isEmpty(cellValue.get('id'))
                            ? cellValue.set('clientId', clientId)
                            : null;
                    default:
                        return cellValue;
                }
            });

            return MasterHomebase.fromJS(updatedHomebaseRow.toJS());
        });
    };

    static getUploadedEntities = ({
        uploadedTable,
        clientId,
        getDataForCell
    }): Immutable.List<MasterHomebase> => {
        const homebasesData = EntityImportHelper.makeDataForEntities({
            table: uploadedTable,
            clientId,
            getDataForCell,
            processEntityRows: HomebaseImportHelper.processEntityRows
        });
        return Immutable.List(homebasesData);
    };

    static generateEntityList = (
        homebasesData: Array<*>
    ): Immutable.List<MasterHomebase> =>
        Immutable.List(
            homebasesData.map(homebaseData =>
                MasterHomebase.fromJS(homebaseData)
            )
        );

    static getCustomFields(entitySettings) {
        return entitySettings?.homebaseSettings.customFields;
    }
    static serializeCrossDockInfo = (
        typesSupported: Array<CrossDocksTypeEnum>
    ): Immutable.List<CrossDocksTypeEnum> => {
        return Immutable.List(typesSupported);
    };

    static processCrossDockInfo = ({
        cellValue
    }: {
        cellValue: ?string
    }): ?Array<CrossDocksTypeEnum | ' '> => {
        if (cellValue) {
            const translatedTypes = CrossDocksType.getOptions({
                translationKey: 'masterHomebaseProperties.crossDockTypes'
            });
            return cellValue.split(',').map(value => {
                const trimmedValue = value.trim();
                const matchedCrossDockType = translatedTypes.find(
                    ({ label }) => label === trimmedValue
                );
                if (matchedCrossDockType) {
                    return matchedCrossDockType.value;
                }
                return ' '; // HACK to handle incorrect value
            });
        }
    };

    static validateCrossDockInfo = ({
        entity
    }: {
        entity: MasterHomebase
    }): ?string => entity.validateCrossDockInfo();

    static validateHigherThresh({ entity, header }) {
        const accessor = header?.accessor?.slice(0, 2);

        const resource = entity?.getIn(accessor);
        return resource?.validateHigherThresh?.() || null;
    }
}
